// BenaPC Hub-connected AI Chatbot
// Connects to Hub API for live data: inventory, repairs, slots
// Uses React Portal to escape transform-based stacking contexts
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bot, Monitor } from 'lucide-react';

const HUB_API = 'https://benapc.duckdns.org:4443/hub';

// A product recommended by the AI buying advisor (comes from the shop's real products.json)
interface AdvisorProduct {
  id: string;
  title: string;
  price: number;
  image?: string;
  url: string; // "/product/<id>"
}

interface Message {
  role: 'user' | 'assistant';
  text: string;
  quickReplies?: string[];
  products?: AdvisorProduct[];
}

interface CustomerData {
  name?: string;
  phone?: string;
  interest?: string;
}

const STORAGE_KEY = 'bpc-chat-session';
// Set once the visitor has sent a message, i.e. once there is a history to load.
const CHATTED_KEY = 'bpc-chat-has-history';

function hasChatted(): boolean {
  try { return localStorage.getItem(CHATTED_KEY) === '1'; } catch { return false; }
}

function markChatted() {
  try { localStorage.setItem(CHATTED_KEY, '1'); } catch { /* private mode etc. */ }
}

function getOrCreateSession(): string {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = 'web-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

const HubChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerData>({});
  const [hasUnread, setHasUnread] = useState(false);
  const [needsHuman, setNeedsHuman] = useState(false);
  const sessionId = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const historyRequested = useRef(false);

  // Load the existing conversation, at most once per page.
  const loadHistory = () => {
    if (historyRequested.current) return;
    historyRequested.current = true;
    fetch(`${HUB_API}/api/public/chat-history/${sessionId.current}`)
      .then(r => r.json())
      .then(d => {
        if (d.messages?.length) {
          const history: Message[] = d.messages.map((m: any) => ({ role: m.role, text: m.text }));
          // If the visitor typed before the history arrived, keep their conversation.
          setMessages(current => (current.some(m => m.role === 'user') ? current : history));
        }
        if (d.customer_data) setCustomerData(prev => ({ ...d.customer_data, ...prev }));
      })
      .catch(() => {});
  };

  useEffect(() => {
    sessionId.current = getOrCreateSession();
    // Only visitors who have chatted before have a history worth fetching up
    // front; everyone else fetches it when they first open the chat. (Until
    // 2026-09-24 this ran on every page load, for every visitor.)
    if (hasChatted()) loadHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome bubble - shows after 5 seconds if user hasn't opened chat
  useEffect(() => {
    if (messages.length === 0) {
      const timer = setTimeout(() => {
        if (!isOpen) setHasUnread(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [messages.length, isOpen]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { role: 'user', text };
    markChatted();
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const r = await fetch(`${HUB_API}/api/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId.current,
          history: messages.slice(-6),
          customer_data: customerData
        })
      });
      const data = await r.json();
      const botMsg: Message = {
        role: 'assistant',
        text: data.reply || 'לא הצלחתי לענות על זה. נסו לנסח אחרת, או כתבו לבניהו בוואטסאפ: 054-667-2863.',
        quickReplies: data.quick_replies || [],
        products: Array.isArray(data.products) ? data.products : []
      };
      setMessages(m => [...m, botMsg]);
      if (data.collected_data) {
        setCustomerData(prev => ({ ...prev, ...Object.fromEntries(Object.entries(data.collected_data).filter(([_, v]) => v)) }));
      }
      setNeedsHuman(!!data.needs_human);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: 'אין חיבור לעוזר כרגע. אפשר לכתוב לבניהו בוואטסאפ או להתקשר: 054-667-2863.' }]);
    }
    setLoading(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setHasUnread(false);
    loadHistory();
    if (messages.length === 0) {
      setTimeout(() => {
        // The history may have arrived in the meantime; don't overwrite it.
        setMessages(current => (current.length ? current : [{
          role: 'assistant',
          text: 'שלום, אני העוזר הדיגיטלי של בנה מחשבים. במה אפשר לעזור?',
          quickReplies: ['רוצים לקנות מחשב', 'תיקון מחשב', 'בדיקת סטטוס תיקון', 'תיאום הגעה למעבדה']
        }]));
      }, 300);
    }
  };

  // Portal target — render directly to body to escape any transform parents
  if (typeof document === 'undefined') return null;

  const content = (
    <>
      {/* Floating button — bottom-right, under the accessibility button
          (2026-10-06: one tidy stack per side, clear of the safe area). */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="bg-[var(--cin-blue)] text-white rounded-full w-[52px] h-[52px] shadow-[0_8px_24px_-8px_rgba(30,85,217,0.6)] hidden md:flex items-center justify-center transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:scale-105 active:scale-95"
          style={{
            position: 'fixed',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            right: '16px',
            zIndex: 9990,
            direction: 'rtl'
          }}
          aria-label="פתיחת הצ'אט עם העוזר הדיגיטלי"
        >
          <svg className="w-6 h-6" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[11px] rounded-full w-5 h-5 flex items-center justify-center font-bold ring-2 ring-white">1</span>
          )}
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          style={{
            position: 'fixed',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            right: '16px',
            width: 'min(calc(100vw - 2rem), 384px)',
            height: 'min(600px, 80vh)',
            zIndex: 9991,
            direction: 'rtl'
          }}
        >
          {/* Header */}
          <div className="bg-[var(--cin-blue)] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center"><Bot className="w-5 h-5" aria-hidden="true" /></div>
              <div>
                <div className="font-bold">העוזר הדיגיטלי</div>
                <div className="text-xs opacity-90 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  מקוון עכשיו
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} aria-label="סגירת הצ'אט" className="text-white/80 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i}>
                <div className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] ${
                      m.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
                {/* Advisor picks — link through to the spec page, nothing to buy */}
                {m.products && m.products.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {m.products.map((p) => (
                      <div
                        key={p.id}
                        className="flex gap-2.5 bg-white border border-gray-200 rounded-xl p-2.5 shadow-sm"
                      >
                        <Link
                          to={p.url}
                          onClick={() => setIsOpen(false)}
                          className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center"
                        >
                          {p.image ? (
                            <img src={p.image} alt={p.title} className="w-full h-full object-contain" loading="lazy" />
                          ) : (
                            <Monitor className="w-6 h-6 text-gray-400" aria-hidden="true" />
                          )}
                        </Link>
                        <div className="flex-1 min-w-0 flex flex-col">
                          <Link
                            to={p.url}
                            onClick={() => setIsOpen(false)}
                            className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 hover:text-blue-700"
                          >
                            {p.title}
                          </Link>
                          <div className="mt-auto flex items-center justify-end pt-1">
                            <Link
                              to={p.url}
                              onClick={() => setIsOpen(false)}
                              className="px-3 py-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold transition-colors"
                            >
                              למפרט המלא
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                    <Link
                      to="/diagnose"
                      onClick={() => setIsOpen(false)}
                      className="block text-center text-xs font-medium text-blue-700 hover:underline pt-0.5"
                    >
                      לא בטוחים? ייעוץ חינם ←
                    </Link>
                  </div>
                )}
                {m.quickReplies && m.quickReplies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 justify-end">
                    {m.quickReplies.map((q, j) => (
                      <button
                        key={j}
                        onClick={() => sendMessage(q)}
                        className="px-3 py-1.5 bg-white border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 text-blue-700 rounded-full text-xs font-medium transition"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-end">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Human escalation CTA — shown when the AI signals it needs a human */}
          {needsHuman && (
            <a
              href="https://wa.me/972546672863?text=%D7%94%D7%99%D7%99%2C%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%A6%27%D7%90%D7%98%20%D7%91%D7%90%D7%AA%D7%A8%20%D7%95%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A2%D7%96%D7%A8%D7%94%20%D7%9E%D7%91%D7%A0%D7%99%D7%94%D7%95"
              target="_blank"
              rel="noopener noreferrer"
              data-track-from="chatbot"
              className="mx-3 mb-1 mt-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-full py-2.5 text-sm font-bold transition-colors shadow-sm"
            >
              שליחת הודעה לבניהו בוואטסאפ
            </a>
          )}

          {/* Input */}
          <div className="border-t border-gray-200 p-3 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
                placeholder="כתבו הודעה…"
                aria-label="ההודעה שלכם"
                disabled={loading}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="שליחת ההודעה"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" transform="scale(-1,1) translate(-24,0)" />
                </svg>
              </button>
            </div>
            <div className="text-center text-[10px] text-gray-400 mt-2">עוזר AI של בנה מחשבים · בניהו: 054-667-2863</div>
          </div>
        </div>
      )}
    </>
  );

  return createPortal(content, document.body);
};

export default HubChatbot;
